import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Layers, Loader2, AlertTriangle, CheckCircle2, RotateCw } from 'lucide-react';
import { albumsApi } from '../../api/albums';
import styles from './FaceIndexStatus.module.css';

interface NonLiveProcessingStatusProps {
  albumId: string;
  /** When true, suppresses the header + progress bar (LiveAlbumStats handles those)
   *  and only renders for actionable states: failed, stuck, or active (force restart). */
  isLive?: boolean;
}

type Status = 'idle' | 'processing' | 'ready' | 'failed';

interface ProcessingState {
  processingStatus: Status;
  hasOverlays: boolean;
  processedCount: number;
  pendingCount: number;
  failedCount: number;
  total: number;
}

const POLL_RUNNING_MS = 3_000;
const POLL_IDLE_MS = 30_000;

// How long (ms) with no progress before we consider a run "stuck"
const STUCK_THRESHOLD_MS = 60_000;

const MAX_AUTO_RETRIES = 5;

export function NonLiveProcessingStatus({ albumId, isLive }: NonLiveProcessingStatusProps) {
  const [state, setState] = useState<ProcessingState | null>(null);
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Synchronous guard — `retrying` state takes a render to flip the button's
  // `disabled` attribute, so a double-click within the same paint can fire two
  // network requests before the visual disable lands. The ref blocks the
  // second call immediately. The backend has its own atomic guard for safety,
  // but stopping it here avoids the wasted round-trip.
  const retryingRef = useRef(false);

  // Auto-retry tracking
  const autoRetryCountRef = useRef(0);
  const wasFailedRef = useRef(false); // detects new failure transitions

  // Track when the processedCount last changed while status is 'processing'
  const lastProgressRef = useRef<{ count: number; at: number }>({ count: -1, at: Date.now() });
  const [isStuck, setIsStuck] = useState(false);

  const doRetry = async (isAuto = false) => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    try {
      const r = await albumsApi.retryProcessing(albumId);
      if (!isAuto) {
        toast.success(r.alreadyRunning ? 'Already restarting…' : 'Overlay processing restarted');
      } else {
        toast.success(`Auto-retrying overlay processing (attempt ${autoRetryCountRef.current}/${MAX_AUTO_RETRIES})`, { duration: 3000 });
      }
      lastProgressRef.current = { count: 0, at: Date.now() };
      setIsStuck(false);
      wasFailedRef.current = false;
      await fetchStatus();
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Could not restart processing';
      if (!isAuto) toast.error(msg);
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const data = await albumsApi.getProcessingStatus(albumId);
      // Update stuck detection: reset timer whenever count advances
      if (data.processingStatus === 'processing') {
        if (data.processedCount !== lastProgressRef.current.count) {
          // Progress made — reset auto-retry counter
          if (data.processedCount > 0) autoRetryCountRef.current = 0;
          lastProgressRef.current = { count: data.processedCount, at: Date.now() };
          setIsStuck(false);
          wasFailedRef.current = false;
        } else if (
          lastProgressRef.current.count !== -1 &&
          Date.now() - lastProgressRef.current.at > STUCK_THRESHOLD_MS
        ) {
          setIsStuck(true);
        }
      } else {
        setIsStuck(false);
        lastProgressRef.current = { count: -1, at: Date.now() };
      }

      // Detect failure transitions and auto-retry
      const isFailed =
        data.processingStatus === 'failed' ||
        data.failedCount > 0 ||
        (data.processingStatus === 'idle' && data.hasOverlays && data.total > 0 && data.processedCount < data.total);

      if (isFailed && !wasFailedRef.current) {
        wasFailedRef.current = true;
        if (autoRetryCountRef.current < MAX_AUTO_RETRIES) {
          autoRetryCountRef.current += 1;
          setTimeout(() => doRetry(true), 1500);
        }
      } else if (!isFailed) {
        wasFailedRef.current = false;
      }

      setState(data);
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchStatus();
      if (cancelled) return;
      const isRunning = data?.processingStatus === 'processing';
      const delay = isRunning ? POLL_RUNNING_MS : POLL_IDLE_MS;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || !state.hasOverlays) return null;

  const { processingStatus, processedCount, total, failedCount } = state;
  const percent = total > 0 ? Math.min(100, Math.round((processedCount / total) * 100)) : 0;
  const remaining = Math.max(0, total - processedCount);

  type DerivedStatus = 'processing' | 'failed' | 'ready' | 'empty';
  const effectiveStatus: DerivedStatus =
    total === 0 ? 'empty'
    // If every photo is already processed, treat as ready regardless of the
    // DB status field — handles the case where the Lambda OOM'd after compositing
    // all photos but before writing processingStatus:'ready'.
    : processedCount >= total && failedCount === 0 ? 'ready'
    : processingStatus === 'processing' ? 'processing'
    : processingStatus === 'failed' || failedCount > 0 ? 'failed'
    : processingStatus === 'ready' ? 'ready'
    // 'idle' with overlays + photos but not processed → treat as failed so the
    // creator can retry. This covers albums whose Lambda crashed silently.
    : 'failed';

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await doRetry(false);
  };

  const tone: 'failed' | 'running' | 'ready' | 'idle' =
    effectiveStatus === 'failed' ? 'failed'
    : effectiveStatus === 'processing' ? 'running'
    : effectiveStatus === 'ready' ? 'ready'
    : 'idle';

  // Show the primary retry row for failures or stuck processing
  const showRetry = effectiveStatus === 'failed' || isStuck;
  const autoRetriesExhausted = autoRetryCountRef.current >= MAX_AUTO_RETRIES;

  // For live albums, LiveAlbumStats already renders the progress bar.
  // Only surface actionable states here (failed, stuck, active force-restart).
  if (isLive) {
    if (effectiveStatus === 'empty' || effectiveStatus === 'ready') return null;
    if (!showRetry && effectiveStatus !== 'processing') return null;

    return (
      <div className={`${styles['block']} ${styles[`tone-${tone}`]}`} onClick={(e) => e.stopPropagation()}>
        {showRetry && (
          <div className={styles['error-row']}>
            <span className={styles['error-text']}>
              {isStuck && effectiveStatus === 'processing'
                ? 'Processing appears stuck'
                : failedCount > 0
                  ? `${failedCount.toLocaleString()} photo${failedCount === 1 ? '' : 's'} failed`
                  : 'Processing did not complete'}
            </span>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className={styles['retry-button']}
            >
              <RotateCw className={`${styles['icon-xs']} ${retrying ? styles['spin'] : ''}`} aria-hidden />
              {retrying ? 'Starting…' : isStuck ? 'Force Retry' : 'Retry'}
            </button>
          </div>
        )}
        {effectiveStatus === 'processing' && !isStuck && (
          <div className={styles['error-row']}>
            <span className={styles['error-text']} style={{ opacity: 0.55, fontSize: '0.7rem' }}>
              Takes a few minutes for large albums
            </span>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className={styles['retry-button']}
              style={{ opacity: 0.7 }}
            >
              <RotateCw className={`${styles['icon-xs']} ${retrying ? styles['spin'] : ''}`} aria-hidden />
              {retrying ? 'Starting…' : 'Force Restart'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles['block']} ${styles[`tone-${tone}`]}`} onClick={(e) => e.stopPropagation()}>
      {/* Header — icon + label + status pill */}
      <div className={styles['header-row']}>
        <span className={styles['label-group']}>
          <Layers className={styles['icon-sm']} aria-hidden />
          <span className={styles['label-text']}>Overlays</span>
        </span>

        <span className={styles['header-spacer']} />

        {effectiveStatus === 'processing' && (
          <span className={`${styles['pill']} ${styles['pill-running']}`}>
            <Loader2 className={`${styles['icon-xs']} ${styles['spin']}`} aria-hidden />
            {isStuck ? 'Stuck' : 'Processing'}
          </span>
        )}
        {effectiveStatus === 'failed' && (
          <span className={`${styles['pill']} ${styles['pill-failed']}`}>
            <AlertTriangle className={styles['icon-xs']} aria-hidden />
            Action needed
          </span>
        )}
        {effectiveStatus === 'ready' && (
          <span className={`${styles['pill']} ${styles['pill-ready']}`}>
            <CheckCircle2 className={styles['icon-xs']} aria-hidden />
            Ready
          </span>
        )}
        {effectiveStatus === 'empty' && (
          <span className={`${styles['pill']} ${styles['pill-muted']}`}>No photos</span>
        )}
      </div>

      {/* Progress numerals + bar */}
      {total > 0 && (
        <>
          <div className={styles['count-row']}>
            <span className={styles['count-strong']}>{processedCount.toLocaleString()}</span>
            <span className={styles['count-divider']}>/</span>
            <span className={styles['count-total']}>{total.toLocaleString()}</span>
            <span className={styles['count-label']}>
              {effectiveStatus === 'failed' || isStuck
                ? 'photos processed'
                : remaining > 0
                  ? `· ${remaining.toLocaleString()} remaining`
                  : 'photos processed'}
            </span>
            <span className={styles['count-spacer']} />
            <span className={styles['percent']}>{percent}%</span>
          </div>

          <div className={styles['progress-track']} aria-hidden>
            <motion.div
              className={`${styles['progress-fill']} ${effectiveStatus === 'failed' || isStuck ? styles['progress-fill-failed'] : ''}`}
              initial={false}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </>
      )}

      {/* Primary retry row — shown for failures and stuck runs */}
      {showRetry && (
        <div className={styles['error-row']}>
          <span className={styles['error-text']}>
            {isStuck && effectiveStatus === 'processing'
              ? 'Processing appears stuck'
              : failedCount > 0
                ? `${failedCount.toLocaleString()} photo${failedCount === 1 ? '' : 's'} failed`
                : autoRetriesExhausted
                  ? `Auto-retry failed after ${MAX_AUTO_RETRIES} attempts`
                  : 'Processing did not complete'}
          </span>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className={styles['retry-button']}
          >
            <RotateCw className={`${styles['icon-xs']} ${retrying ? styles['spin'] : ''}`} aria-hidden />
            {retrying ? 'Starting…' : isStuck ? 'Force Retry' : 'Retry'}
          </button>
        </div>
      )}

      {/* During active (non-stuck) processing, offer a Force Restart escape hatch */}
      {effectiveStatus === 'processing' && !isStuck && (
        <div className={styles['error-row']}>
          <span className={styles['error-text']} style={{ opacity: 0.55, fontSize: '0.7rem' }}>
            Takes a few minutes for large albums
          </span>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className={styles['retry-button']}
            style={{ opacity: 0.7 }}
          >
            <RotateCw className={`${styles['icon-xs']} ${retrying ? styles['spin'] : ''}`} aria-hidden />
            {retrying ? 'Starting…' : 'Force Restart'}
          </button>
        </div>
      )}
    </div>
  );
}
